import { Popover } from "antd";
import { DownOutlined, LoadingOutlined, LogoutOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "../store/store";
import { LocalStorageKey } from "../constants/localStorageKeys";
// CRA's ModuleScopePlugin whitelists package.json, so the shipped version can be read
// straight from it — no build-time env var to keep in sync.
import packageJson from "../../package.json";

const AVATAR_PATH = "/ailbl/users/avatars";

const getAvatarUrl = (): string =>
{
  try
  {
    const config = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
    );
    const baseUrl: string = config?.REACT_APP_API_BASE_URL ?? "";
    return `${baseUrl}${AVATAR_PATH}`;
  } catch
  {
    return AVATAR_PATH;
  }
};

export default function UserMenu()
{
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [avatarOk, setAvatarOk] = useState(true);

  const whoami = useSelector((state: RootState) => state.appCommonSlice.whoami);
  const traits: any = whoami?.identity?.traits ?? {};
  const firstName: string = traits?.name?.first ?? "";
  const lastName: string = traits?.name?.last ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || t("User");
  const email: string = traits?.email ?? "";
  const initial = (firstName?.[0] || email?.[0] || "U").toUpperCase();



  const authMethod: string | undefined = (() =>
  {
    try
    {
      return JSON.parse(
        localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
      )?.REACT_APP_AUTHENTICATION_METHOD;
    } catch
    {
      return undefined;
    }
  })();

  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () =>
  {
    if (loggingOut) return;
    setLoggingOut(true);

    // Clear auth data...
    localStorage.removeItem("token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("key");

    // ...show the loading state briefly, then go to /login WITHOUT a full page
    // reload (client-side nav avoids the blank/white flash of window.location).
    await new Promise((r) => setTimeout(r, 400));

    setOpen(false);
    setLoggingOut(false);
    navigate("/login", { replace: true });
  };

  const handleProfileClick = () =>
  {
    try
    {
      const config = JSON.parse(
        localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
      );
      const profileUrl = config?.REACT_APP_ENDPOINT_ACCOUNT;
      if (profileUrl)
      {
        window.open(profileUrl, "_blank");
      }
    } catch
    {
      // ignore
    }
    setOpen(false);
  };

  const renderAvatar = (size: number) => (
    <div
      className="rounded-full bg-[#8B5CF6] flex items-center justify-center text-white font-semibold relative overflow-hidden flex-shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: Math.floor(size / 2.5),
      }}
    >
      {avatarOk && (
        <img
          src={getAvatarUrl()}
          alt={fullName}
          onError={() => setAvatarOk(false)}
          className="w-full h-full object-cover absolute inset-0"
        />
      )}
      {!avatarOk && <span>{initial}</span>}
    </div>
  );

  const content = (
    <div className="w-[260px] py-1">
      {/* OAUTH2 doesn't fetch whoami, so there's no user profile to show —
          render only the logout button. Other auth methods keep the info row. */}
      {authMethod !== "OAUTH2" && (
        <div className="flex items-center gap-3 px-3 py-2">
          {renderAvatar(40)}
          <div className="min-w-0 flex-1">
            <p className="m-0 font-semibold text-[var(--color-text-primary)] truncate">
              {fullName}
            </p>
            <p className="m-0 text-xs text-[var(--color-text-tertiary)] truncate">
              ({email})
            </p>
          </div>
        </div>
      )}
      {/* <Divider style={{ margin: "4px 0" }} />
      <button
        onClick={handleProfileClick}
        className="w-full text-left px-3 py-2 rounded hover:bg-[var(--neutral-800)] text-[var(--color-text-primary)]"
      >
        {t("Profile")}
      </button> */}

      {authMethod === "OAUTH2" && (
        <>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="w-full text-left px-3 py-2 rounded hover:bg-[var(--neutral-800)] text-[var(--color-text-primary)] flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loggingOut ? <LoadingOutlined /> : <LogoutOutlined />}
            {loggingOut ? t("Logging out...") : t("Logout")}
          </button>
        </>
      )}

      <p className="m-0 px-3 py-2 text-xs text-[var(--color-text-tertiary)]">
        {t("Version")} {packageJson.version}
      </p>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={content}
      trigger="click"
      placement="bottomRight"
      overlayClassName="user-menu-popover"
      getPopupContainer={(triggerNode) =>
        triggerNode.parentElement as HTMLElement
      }
    >
      <div className="flex items-center gap-1 cursor-pointer">
        {renderAvatar(36)}
        <DownOutlined className="text-gray-400 text-xs" />
      </div>
    </Popover>
  );
}
